import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { MessageService } from 'primeng/api';
import { LoginService } from 'src/app/services/login-service/login.service';

import { LOGIN_PAGES } from '../enum-login-pages';
import { MixpanelService } from '../../../services/mixpanel-service/mixpanel.service';

enum VERIFICATION_ERROR_STATUS {
  INVALID_CODE = 0,
  CODE_EXPIRED = 1,
}

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
  @Output() nextStep = new EventEmitter<number>();
  @Input() email = '';
  loading = false;

  verificationCodeError = -1;
  verificationErrorStatus = VERIFICATION_ERROR_STATUS;
  toolTip: string;
  captcha_token = '';

  frmForgotPassword = new FormGroup({
    verificationCode: new FormControl('', Validators.required),
    newPassword: new FormControl('', [
      Validators.required,
      Validators.pattern(
        '(?:(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])|(?=.*[a-z])(?=.*[0-9])(?=.*[$%&!@*#?_-])|(?=.*[A-Z])(?=.*[0-9])(?=.*[$%&!@*#?_-])|(?=.*[A-Z])(?=.*[a-z])(?=.*[$%&!@*#?_-])).{13,}$'
      ),
    ]),
    confirmPassword: new FormControl('', [
      Validators.required,
      Validators.pattern(
        '(?:(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])|(?=.*[a-z])(?=.*[0-9])(?=.*[$%&!@*#?_-])|(?=.*[A-Z])(?=.*[0-9])(?=.*[$%&!@*#?_-])|(?=.*[A-Z])(?=.*[a-z])(?=.*[$%&!@*#?_-])).{13,}$'
      ),
    ]),
  });

  constructor(
    private loginService: LoginService,
    private messageService: MessageService,
    private mixpanelService: MixpanelService
  ) {
    this.toolTip = this.getTooltipContent();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit(): void {}

  getTooltipContent() {
    return `
              <h6>password requirements</h6>
              <ul>
                <li>Must contain at least 13 characters</li>
                <li>Must contain at least 3 of the following:
                  <ul>
                    <li>An uppercase character</li>
                    <li>A lowercase character</li>
                    <li>A number</li>
                    <li>A special character</li>
                  </ul>
                </li>
              </ul>
    `;
  }

  confirm() {
    this.loading = true;
    const userPool = this.loginService.getCognitoUserPool();
    const cognitoUser = new CognitoUser({
      Username: this.email.toLowerCase(),
      Pool: userPool,
    });

    cognitoUser.confirmPassword(
      this.frmForgotPassword.value.verificationCode,
      this.frmForgotPassword.value.newPassword,
      {
        onSuccess: (res) => {
          this.confirmPasswordOnSuccess();
          this.mixpanelService.track({
            id: this.email,
            eventName: 'cognito_verification_code_success',
            eventDisplayName: 'cognito_verification_code_success Button Click',
            kpi: 'Click-Through Rate',
            trigger: 'On Button Click',
            eventDefinition:
              'User clicks the cognito_verification_code_success requested button',
            propertyType: 'Event Property',
            propertyDefinition: 'Button confirmPassword',
          });
        },
        onFailure: (err: any) => {
          this.confirmPasswordOnFailure(err);
        },
      }
    );
  }

  confirmPasswordOnSuccess() {
    this.mixpanelService.track({
      id: this.email,
      eventName: 'account_confirmed_screen',
      eventDisplayName: 'account_confirmed_screen Button Click',
      kpi: 'Click-Through Rate',
      trigger: 'On Button Click',
      eventDefinition:
        'User clicks the account_confirmed_screen requested button',
      propertyType: 'Event Property',
      propertyDefinition: 'Button confirmPasswordOnSuccess',
    });
    this.nextStep.emit(LOGIN_PAGES.ACCOUNT_CONFIRMED_SCREEN);
    this.loading = false;
  }

  confirmPasswordOnFailure(err: any) {
    if (err.code === 'CodeMismatchException') {
      this.verificationCodeError = this.verificationErrorStatus.INVALID_CODE;
      this.mixpanelService.track({
        id: this.email,
        eventName: 'cognito_verification_invalid_code',
        eventDisplayName: 'cognito_verification_invalid_code Button Click',
        kpi: 'Click-Through Rate',
        trigger: 'On Button Click',
        eventDefinition:
          'User clicks the cognito_verification_invalid_code requested button',
        propertyType: 'Event Property',
        propertyDefinition: 'Button confirmPasswordOnFailure',
      });
    } else {
      this.verificationCodeError = this.verificationErrorStatus.CODE_EXPIRED;
      this.mixpanelService.track({
        id: this.email,
        eventName: 'cognito_verification_code_expired',
        eventDisplayName: 'cognito_verification_code_expired Button Click',
        kpi: 'Click-Through Rate',
        trigger: 'On Button Click',
        eventDefinition:
          'User clicks the verification_code_expired requested button',
        propertyType: 'Event Property',
        propertyDefinition: 'Button confirmPassword',
      });
    }
    this.loading = false;
  }

  emailNewCode() {
    const userPool = this.loginService.getCognitoUserPool();
    const cognitoUser = new CognitoUser({
      Username: this.email.toLowerCase(),
      Pool: userPool,
    });
    cognitoUser.forgotPassword({
      onSuccess: () => {
        this.mixpanelService.track({
          id: this.email,
          eventName: 'new_cognito_verification_code_requested',
          eventDisplayName: 'new_cognito_verification_code Button Click',
          kpi: 'Click-Through Rate',
          trigger: 'On Button Click',
          eventDefinition: 'User clicks the verification_code requested button',
          propertyType: 'Event Property',
          propertyDefinition: 'Button emailNewCode',
        });

        this.messageService.add({
          key: 'toast',
          severity: 'info',
          life: 5000,
          detail: 'Sent! Didn’t get an email? Contact your account manager.',
        });
      },
      onFailure: () => {
        this.mixpanelService.track({
          id: this.email,
          eventName: 'new_cognito_verification_code_failed',
          eventDisplayName: 'new_cognito_verification_code Button Click',
          kpi: 'Click-Through Rate',
          trigger: 'On Button Click',
          eventDefinition: 'User clicks the verification_code button',
          propertyType: 'Event Property',
          propertyDefinition: 'Button emailNewCode',
        });
      },
    });
  }

  resetCaptcha() {
    this.captcha_token = '';
  }

  showResponse(event: any) {
    this.mixpanelService.track({
      id: this.email,
      eventName: 'captcha_click',
      eventDisplayName: 'CAPTCHA Button Click',
      kpi: 'Click-Through Rate',
      trigger: 'On Button Click',
      eventDefinition: 'User clicks the CAPTCHA button',
      propertyType: 'Event Property',
      propertyDefinition: 'Button showResponse',
    });

    this.captcha_token = event.response;
  }
}
